import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { BRAND, SOCIALS } from "@/data/site";
import { Wordmark } from "@/components/Navbar";

const NAV = [
  { label: "Programs", path: "/programs", testid: "footer-link-programs" },
  { label: "Performance", path: "/performance", testid: "footer-link-performance" },
  { label: "Recovery", path: "/recovery", testid: "footer-link-recovery" },
  { label: "Corporate B2B", path: "/corporate", testid: "footer-link-corporate" },
  { label: "About", path: "/about", testid: "footer-link-about" },
  { label: "Pricing", path: "/pricing", testid: "footer-link-pricing" },
  { label: "Contact", path: "/contact", testid: "footer-link-contact" },
];

const LEGAL = [
  { label: "Privacy Policy", path: "/legal#privacy", testid: "footer-link-privacy" },
  { label: "Terms of Service", path: "/legal#terms", testid: "footer-link-terms" },
  { label: "Medical Disclaimer", path: "/legal#disclaimer", testid: "footer-link-disclaimer" },
];

export default function Footer() {
  const toTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer data-testid="site-footer" className="border-t border-bone/10 bg-ink px-6 pb-10 pt-20 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-col justify-between gap-12 lg:flex-row">
          <div>
            <Wordmark className="text-2xl" />
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ash">
              {BRAND.descriptor}
            </p>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-ash">
              Private 1:1 performance coaching, delivered live over video. Eliminate pain, build strength, prepare for your biggest goals.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-12 sm:grid-cols-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash">Navigate</p>
              <ul className="mt-5 space-y-3">
                {NAV.map((l) => (
                  <li key={l.path}>
                    <Link
                      to={l.path}
                      data-testid={l.testid}
                      className="text-sm text-bone/80 transition-colors duration-300 hover:text-lime"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash">Legal</p>
              <ul className="mt-5 space-y-3">
                {LEGAL.map((l) => (
                  <li key={l.path}>
                    <Link
                      to={l.path}
                      data-testid={l.testid}
                      className="text-sm text-bone/80 transition-colors duration-300 hover:text-lime"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash">Connect</p>
              <ul className="mt-5 space-y-3">
                {SOCIALS.map((s) => (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-social-${s.label.toLowerCase()}`}
                      className="text-sm text-bone/80 transition-colors duration-300 hover:text-lime"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="footer-email-link"
                    className="text-sm text-bone/80 transition-colors duration-300 hover:text-lime"
                  >
                    {BRAND.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 select-none overflow-hidden">
          <p className="whitespace-nowrap font-display text-[11.5vw] font-black uppercase leading-none tracking-tight text-outline">
            Recover. Build. Perform.
          </p>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-bone/10 pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
            © {new Date().getFullYear()} {BRAND.name} — {BRAND.domain}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
            Live 1:1 video coaching. Global delivery.
          </p>
          <button
            onClick={toTop}
            data-testid="footer-back-to-top"
            className="group flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/80 transition-colors hover:text-lime"
            aria-label="Back to top"
          >
            Back to top
            <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1" />
          </button>
        </div>
      </div>
    </footer>
  );
}
